import { ref, watch, computed, type Ref, type ComputedRef } from 'vue';
import { useRoute, useRouter, type LocationQueryRaw, type LocationQueryValue } from 'vue-router';
import type { PaginationProps } from 'naive-ui';

export type SortDirection = 'asc' | 'desc';

// Column filters are mirrored to the URL under this prefix (e.g. `?f_status=x`).
// A prefix lets the composable round-trip arbitrary, runtime-discovered filter
// keys without the caller having to enumerate them up front.
const FILTER_PREFIX = 'f_';

export interface UseListQuerySyncOptions {
  defaultPageSize?: number;
  allowedPageSizes?: number[];
  defaultSortKey?: string;
  defaultSortDirection?: SortDirection;
  /**
   * Enables a tab param (`?tab=`). Pass the tab that is shown by default — the
   * param is omitted from the URL while `activeTab` equals it. Leave undefined
   * to disable tab syncing entirely.
   */
  defaultTab?: string;
  /** Optional allowlist for the tab param; an unrecognized `?tab=` falls back to `defaultTab`. */
  allowedTabs?: string[];
  /**
   * Renames the tab param for views where `tab` reads wrong — the reviews list
   * spends it on `?view=board|table`. The tab machinery is what a view-mode
   * switch wants: it hydrates synchronously, omits the default value, and is
   * written by the same watcher as `page`/`sort`/`f_*`, so a mode toggle that
   * also resets page and sort still emits a single `router.replace`.
   */
  tabParam?: string;
  /**
   * Enables an archived toggle param (`?archived=`). The value passed is the
   * default (the param is omitted while `includeArchived` equals it). Leave
   * undefined to disable.
   */
  includeArchived?: boolean;
}

export interface UseListQuerySyncReturn {
  currentPage: Ref<number>;
  pageSize: Ref<number>;
  searchQuery: Ref<string>;
  sortKey: Ref<string>;
  sortDirection: Ref<SortDirection>;
  sortActivated: Ref<boolean>;
  /** Only synced to the URL when `defaultTab` was provided. */
  activeTab: Ref<string>;
  /** Only synced to the URL when the `includeArchived` option was provided. */
  includeArchived: Ref<boolean>;
  /**
   * Column filters, mirrored to the URL under the `f_` prefix. Assign a fresh
   * object (e.g. `columnFilters.value = { status: ['pending'] }`) to update —
   * the keys are arbitrary and need not be declared in advance.
   */
  columnFilters: Ref<Record<string, string[]>>;
  /**
   * Ready-made handler for `GaiaFilterPanel`'s `@update:filters` — coerces the raw
   * `Record<string, unknown>` the component emits into `Record<string, string[]>`
   * and stores it in `columnFilters`. Lets a view wire filters with no per-view
   * mapping: `<filter-bar :active-filters="columnFilters" @update:filters="setColumnFilters" />`.
   */
  setColumnFilters: (raw: Record<string, unknown>) => void;
}

type QueryValue = LocationQueryValue | LocationQueryValue[] | undefined;

// Repeated query params (?q=a&q=b) arrive as arrays — take the first value.
function firstValue(value: QueryValue): string | null {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[0] : null;
  }
  return value ?? null;
}

// Normalize a query value (single, repeated, or missing) to a string array.
function toStringArray(value: QueryValue): string[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
  }
  return typeof value === 'string' && value.length > 0 ? [value] : [];
}

function parsePage(value: QueryValue): number {
  const raw = firstValue(value);
  if (raw === null) return 1;
  const parsed = Number.parseInt(raw, 10);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
}

function parsePageSize(value: QueryValue, allowed: number[], fallback: number): number {
  const raw = firstValue(value);
  if (raw === null) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return allowed.includes(parsed) ? parsed : fallback;
}

function parseDirection(value: QueryValue, fallback: SortDirection): SortDirection {
  const raw = firstValue(value);
  return raw === 'asc' || raw === 'desc' ? raw : fallback;
}

/**
 * Keeps list state (page, page size, search text, sort, column filters, and —
 * optionally — a tab and an archived toggle) in sync with the URL query string
 * so it survives navigation away and back (e.g. list → detail → back),
 * refreshes, and shared deep links.
 *
 * The returned refs are hydrated synchronously from the current `route.query`,
 * so callers should use them in place of their own local refs. Changes to any
 * of them are mirrored back into the URL via `router.replace` (defaults omitted
 * to keep clean URLs). Tab/archived stay dormant unless their option is
 * supplied; column filters sync automatically (an empty `columnFilters` writes
 * nothing), so existing callers are unaffected.
 */
export function useListQuerySync(options: UseListQuerySyncOptions = {}): UseListQuerySyncReturn {
  const {
    defaultPageSize = 10,
    allowedPageSizes = [5, 10, 25, 50],
    defaultSortKey = 'created_at',
    defaultSortDirection = 'desc',
    defaultTab,
    allowedTabs,
    tabParam = 'tab',
    includeArchived: defaultIncludeArchived,
  } = options;

  const tabEnabled = defaultTab !== undefined;
  const archivedEnabled = defaultIncludeArchived !== undefined;

  const route = useRoute();
  const router = useRouter();

  // Sort is considered active when a `sort` param is present in the URL.
  const initialSort = firstValue(route.query.sort);
  const hasSort = initialSort !== null && initialSort !== '';

  const currentPage = ref<number>(parsePage(route.query.page));
  const pageSize = ref<number>(
    parsePageSize(route.query.pageSize, allowedPageSizes, defaultPageSize)
  );
  const searchQuery = ref<string>(firstValue(route.query.q) ?? '');
  const sortKey = ref<string>(hasSort ? initialSort : defaultSortKey);
  const sortDirection = ref<SortDirection>(
    hasSort ? parseDirection(route.query.dir, defaultSortDirection) : defaultSortDirection
  );
  const sortActivated = ref<boolean>(hasSort);

  const activeTab = ref<string>(resolveInitialTab());
  const includeArchived = ref<boolean>(resolveInitialArchived());
  const columnFilters = ref<Record<string, string[]>>(resolveInitialFilters());

  function resolveInitialTab(): string {
    const fallback = defaultTab ?? '';
    if (!tabEnabled) return fallback;
    const raw = firstValue(route.query[tabParam]);
    if (raw === null) return fallback;
    if (allowedTabs && !allowedTabs.includes(raw)) return fallback;
    return raw;
  }

  function resolveInitialArchived(): boolean {
    const fallback = defaultIncludeArchived ?? false;
    if (!archivedEnabled) return fallback;
    const raw = firstValue(route.query.archived);
    if (raw === null) return fallback;
    return raw === 'true';
  }

  function resolveInitialFilters(): Record<string, string[]> {
    const result: Record<string, string[]> = {};
    for (const queryKey of Object.keys(route.query)) {
      if (!queryKey.startsWith(FILTER_PREFIX)) continue;
      const values = toStringArray(route.query[queryKey]);
      if (values.length > 0) {
        result[queryKey.slice(FILTER_PREFIX.length)] = values;
      }
    }
    return result;
  }

  function setColumnFilters(raw: Record<string, unknown>): void {
    const next: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (Array.isArray(value) && value.length > 0) {
        next[key] = value.map(entry => String(entry));
      }
    }
    columnFilters.value = next;
  }

  // Guard against writing the query while a navigation to another route is in
  // flight (e.g. a debounced search callback firing after the row click).
  const ownedRouteName = route.name;

  watch(
    [
      currentPage,
      pageSize,
      searchQuery,
      sortKey,
      sortDirection,
      sortActivated,
      activeTab,
      includeArchived,
      columnFilters,
    ],
    () => {
      if (route.name !== ownedRouteName) return;

      const query: LocationQueryRaw = { ...route.query };
      delete query.page;
      delete query.pageSize;
      delete query.q;
      delete query.sort;
      delete query.dir;
      if (tabEnabled) delete query[tabParam];
      if (archivedEnabled) delete query.archived;
      for (const queryKey of Object.keys(query)) {
        if (queryKey.startsWith(FILTER_PREFIX)) delete query[queryKey];
      }

      if (currentPage.value > 1) query.page = String(currentPage.value);
      if (pageSize.value !== defaultPageSize) query.pageSize = String(pageSize.value);
      if (searchQuery.value) query.q = searchQuery.value;
      if (sortActivated.value) {
        query.sort = sortKey.value;
        query.dir = sortDirection.value;
      }
      if (tabEnabled && activeTab.value !== defaultTab) query[tabParam] = activeTab.value;
      if (archivedEnabled && includeArchived.value !== defaultIncludeArchived) {
        query.archived = String(includeArchived.value);
      }
      for (const [filterKey, values] of Object.entries(columnFilters.value)) {
        if (values.length > 0) query[FILTER_PREFIX + filterKey] = [...values];
      }

      void router.replace({ query });
    }
  );

  return {
    currentPage,
    pageSize,
    searchQuery,
    sortKey,
    sortDirection,
    sortActivated,
    activeTab,
    includeArchived,
    columnFilters,
    setColumnFilters,
  };
}

export interface UsePaginationConfigOptions {
  pageSizes?: number[];
  showSizePicker?: boolean;
}

/**
 * Builds the Naive-UI pagination config object from the refs `useListQuerySync`
 * owns, so views don't hand-roll `paginationConfig` + page/size handlers. Changing
 * the page size resets to page 1 (the app-wide behavior). Views keep their own
 * `watch(currentPage)` / `watch(pageSize)` fetch triggers — this only builds config.
 */
export function usePaginationConfig(
  currentPage: Ref<number>,
  pageSize: Ref<number>,
  itemCount: Ref<number>,
  options: UsePaginationConfigOptions = {}
): ComputedRef<PaginationProps> {
  const { pageSizes = [5, 10, 25, 50], showSizePicker = true } = options;

  return computed<PaginationProps>(() => ({
    page: currentPage.value,
    pageSize: pageSize.value,
    itemCount: itemCount.value,
    showSizePicker,
    pageSizes,
    onUpdatePage: (page: number): void => {
      currentPage.value = page;
    },
    onUpdatePageSize: (size: number): void => {
      pageSize.value = size;
      currentPage.value = 1;
    },
  }));
}
